import { useEffect, useRef } from 'react';

/**
 *
 * @param {Object} params
 * @param {Array<{id: string, label: string}>} params.options - options de l'étape (ids possiblement « uuid1,uuid2 »)
 * @param {boolean} params.isLoading - chargement react-query en cours
 * @param {Array<string>} params.value - ids déjà sélectionnés (URL)
 * @param {(option: Object) => void} params.onSelect - commit de la sélection
 * @param {(() => void)|undefined} params.onAdvance - passage à l'étape suivante (undefined sur la dernière étape)
 */
export function useAutoSelectSingleOption({ options, isLoading, value, onSelect, onAdvance }) {
  const firedForRef = useRef(null);
  // The advance is deferred so the selection commits first. The timer is kept
  // in a ref and cleared ONLY on unmount — NOT in the effect cleanup. Otherwise
  // onSelect changes `value`, the effect re-runs, React runs the cleanup, and
  // the pending advance timer would be cancelled before it ever fires (the
  // auto-select would work but the auto-advance never would — SP-430 M10).
  const advanceTimerRef = useRef(null);

  useEffect(
    () => () => {
      if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current);
    },
    []
  );

  useEffect(() => {
    if (isLoading) return;
    if (!options || options.length !== 1) return;
    if (value && value.length > 0) return;

    const onlyOption = options[0];
    if (!onlyOption || firedForRef.current === onlyOption.id) return;
    firedForRef.current = onlyOption.id;

    onSelect(onlyOption);
    if (onAdvance) {
      // Let the selection commit before advancing (same pattern as the
      // autoProgress of StepContent). The timer survives the re-render caused
      // by onSelect updating `value` (see advanceTimerRef note above).
      if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current);
      advanceTimerRef.current = setTimeout(onAdvance, 100);
    }
  }, [options, isLoading, value, onSelect, onAdvance]);
}
